import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import EntityForm from '../components/EntityForm/EntityForm';
import formStyles from '../components/EntityForm/EntityForm.module.css';
import {
  getBooking,
  getSetting,
  updateBooking,
  archiveBooking,
  cancelBooking,
  recheckBooking,
  approveBooking,
  isAdmin as checkAdmin,
  isSupervisorOrAdmin,
} from '../api';
import useEntityForm from '../hooks/useEntityForm';
import PlayerRow from '../components/PlayerRow';
import HistoryAccordion from '../components/HistoryAccordion/HistoryAccordion';
import BookingInfoPanel from '../components/BookingInfoPanel/BookingInfoPanel';
import styles from './BookingFormPage.module.css';
import { computeEstArrival, parseISOTime, formatHourMin, formatISODate, formatDateTime } from '../utils';
import { useToasts } from '../hooks/useToasts';

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function formatDateFull(isoStr) {
  if (!isoStr) return '—';
  const datePart = formatISODate(isoStr);
  if (datePart === '—' || datePart === isoStr) return datePart;
  const { hour, min } = parseISOTime(isoStr);
  const hasTime = isoStr.match(/T(\d{2}):(\d{2})/);
  if (!hasTime) return datePart;
  return `${datePart} · ${formatHourMin(hour, min)}`;
}


// ---------------------------------------------------------------------------
// BookingFormPage
// ---------------------------------------------------------------------------

function emptyPlayer() {
  return { type: 'member', memberNumber: '', memberName: '', guestName: '', needsCart: false, holes: null, status: 'Booked', memberId: null };
}

function playerFromApi(p) {
  const isMember = Boolean(p.member_number);
  return {
    id: p.id,
    type: isMember ? 'member' : 'guest',
    memberNumber: p.member_number || '',
    memberName: p.member_name || '',
    guestName: p.guest_name || '',
    needsCart: p.needs_golf_cart || false,
    holes: p.holes_to_play || null,
    status: p.status || 'Booked',
    memberId: p.member_id || null,
  };
}

export default function BookingFormPage({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = checkAdmin(user);
  const { toasts, addToast } = useToasts();

  // Read-only header info
  const [bookingNumber, setBookingNumber] = useState('');
  const [teeTimeLabel, setTeeTimeLabel] = useState('');
  const [teeTimeHole, setTeeTimeHole] = useState(null);
  const [teeTimeStartMins, setTeeTimeStartMins] = useState(null);
  const [nineHolePace, setNineHolePace] = useState(60);
  const [isProblematic, setIsProblematic] = useState(false);
  const [isValidated, setIsValidated] = useState(true);
  const [warnings, setWarnings] = useState([]);
  const [warningsLastChecked, setWarningsLastChecked] = useState(null);

  // Editable fields
  const [bookedByMemberId, setBookedByMemberId] = useState(null);
  const [players, setPlayers] = useState([emptyPlayer()]);

  const [isCancelled, setIsCancelled] = useState(false);

  // Form state
  const [fieldErrors, setFieldErrors] = useState({});
  const [isCancelling, setIsCancelling] = useState(false);

  const {
    isBusy,
    isLoading,
    formError,
    setFormError,
    setIsLoading,
    isArchived,
    setIsArchived,
    pageTitle,
    setPageTitle,
    handleCancel,
    handleArchive,
    handleRestore,
    runSubmit,
  } = useEntityForm({
    id,
    backPath: '/bookings',
    archiveFn: archiveBooking,
    unarchiveFn: (entityId) => updateBooking(entityId, { is_archived: false }),
  });

  useEffect(() => {
    setIsLoading(true);
    Promise.allSettled([getBooking(id), getSetting('nine_hole_pace')])
      .then(([bookingResult, paceResult]) => {
        if (bookingResult.status === 'fulfilled') {
          const b = bookingResult.value.booking;
          setBookingNumber(b.booking_number || '');
          setIsProblematic(b.is_problematic || false);
          setIsValidated(b.is_validated !== false);
          setIsArchived(b.is_archived || false);
          setIsCancelled(b.is_cancelled || false);
          setBookedByMemberId(b.booked_by_member_id || null);
          setWarnings(b.warnings || []);
          setWarningsLastChecked(b.warnings_last_checked_at || null);

          const tt = b.tee_time || {};
          const parts = [];
          if (tt.start_time) parts.push(formatDateFull(tt.start_time));
          if (tt.golf_course_name) parts.push(tt.golf_course_name);
          if (tt.hole != null) parts.push(`Hole ${tt.hole}`);
          setTeeTimeLabel(parts.join(' · '));
          setTeeTimeHole(tt.hole != null ? tt.hole : null);
          const { hour: _h, min: _m } = parseISOTime(tt.start_time || '');
          setTeeTimeStartMins(tt.start_time ? _h * 60 + _m : null);

          const mapped = (b.players || []).map(playerFromApi);
          setPlayers(mapped.length > 0 ? mapped : [emptyPlayer()]);
          setPageTitle(`Booking ${b.booking_number || id}`);
        } else {
          setFormError(bookingResult.reason?.message || 'Failed to load booking.');
          setPageTitle('Booking');
        }
        if (paceResult.status === 'fulfilled') {
          const val = parseInt(paceResult.value?.setting?.value, 10);
          if (!isNaN(val) && val > 0) setNineHolePace(val);
        }
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const handlePlayerChange = useCallback((idx, patch) => {
    setPlayers((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
    if (fieldErrors.players) setFieldErrors((prev) => ({ ...prev, players: '' }));
  }, [fieldErrors.players]);

  const handleMemberSelect = useCallback((idx, member) => {
    if (!member) {
      const deselectedMemberId = players[idx]?.memberId;
      handlePlayerChange(idx, { memberNumber: '', memberName: '', memberId: null });
      if (deselectedMemberId && deselectedMemberId === bookedByMemberId) {
        const nextMember = players.find((p, i) => i !== idx && p.type === 'member' && p.memberId);
        setBookedByMemberId(nextMember?.memberId ?? null);
      }
      return;
    }
    handlePlayerChange(idx, { memberNumber: member.member_number, memberName: member.member_name, memberId: member.id });
    if (bookedByMemberId === null) setBookedByMemberId(member.id);
  }, [handlePlayerChange, players, bookedByMemberId]);

  const handleAddPlayer = useCallback(() => {
    setPlayers((prev) => [...prev, emptyPlayer()]);
  }, []);

  const handleRemovePlayer = useCallback((idx) => {
    setPlayers((prev) => {
      const removedPlayer = prev[idx];
      const newList = prev.filter((_, i) => i !== idx);
      if (removedPlayer?.memberId === bookedByMemberId) {
        const nextMember = newList.find((p) => p.type === 'member' && p.memberId);
        setBookedByMemberId(nextMember?.memberId ?? null);
      }
      return newList;
    });
  }, [bookedByMemberId]);

  const validate = useCallback(() => {
    const errors = {};
    const hasMember = players.some(
      (p) => p.type === 'member' && p.memberNumber
    );
    if (!hasMember) {
      errors.players = 'At least one player must be a member with a valid member number.';
    }
    const missingHoles = players.some((p) => p.holes === null || p.holes === undefined);
    if (!errors.players && missingHoles) {
      errors.players = 'Please select the number of holes for every player.';
    }
    return errors;
  }, [players]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setFormError('');
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    await runSubmit(() => updateBooking(id, {
      ...(bookedByMemberId !== null ? { booked_by_member_id: bookedByMemberId } : {}),
      players: players.map((p) => {
        const base = {
          needs_golf_cart: p.needsCart,
          holes_to_play: p.holes,
          status: p.status || 'Booked',
        };
        if (p.id) base.id = p.id;
        if (p.type === 'member') {
          base.member_number = p.memberNumber;
        } else {
          base.guest_name = p.guestName || 'Guest';
        }
        return base;
      }),
    }));
  }, [id, bookedByMemberId, players, validate, runSubmit, setFormError]);

  const handleRecheck = useCallback(async () => {
    try {
      const data = await recheckBooking(id);
      const b = data.booking;
      setIsProblematic(b.is_problematic || false);
      setWarnings(b.warnings || []);
      setWarningsLastChecked(b.warnings_last_checked_at || null);
    } catch (err) {
      setFormError(err.message || 'Recheck failed.');
    }
  }, [id]);

  const handleApprove = useCallback(async () => {
    try {
      const data = await approveBooking(id);
      const b = data.booking;
      setIsProblematic(b.is_problematic || false);
      setWarnings(b.warnings || []);
      setWarningsLastChecked(b.warnings_last_checked_at || null);
      addToast('Booking approved.');
    } catch (err) {
      setFormError(err.message || 'Approval failed.');
    }
  }, [id, addToast]);

  const handleCancelBooking = useCallback(async () => {
    setIsCancelling(true);
    setFormError('');
    try {
      await cancelBooking(id);
      addToast('Booking cancelled.');
      navigate('/bookings');
    } catch (err) {
      setFormError(err.message || 'Failed to cancel booking.');
    } finally {
      setIsCancelling(false);
    }
  }, [id, addToast, navigate, setFormError]);

  return (
    <>
      <EntityForm
        title={pageTitle}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        submitLabel="Save changes"
        isSubmitting={isBusy}
        error={formError}
        canDelete={isAdmin && !isArchived}
        onDelete={handleArchive}
        confirmMessage={`Archive booking ${bookingNumber}? This will remove it from active use.`}
        canRestore={isAdmin && isArchived}
        onRestore={() => handleRestore(`Restore booking ${bookingNumber}? It will become active again.`)}
        restoreLabel="Restore booking"
        canCancelBooking={!!id && !isArchived}
        onCancelBooking={handleCancelBooking}
        cancelBookingConfirmMessage="Are you sure you want to cancel this booking? This cannot be undone."
        isCancellingBooking={isCancelling}
        belowCard={<HistoryAccordion entityPath={`/bookings/${id}`} />}
      >
        {/* Read-only booking header + warnings */}
            {!isLoading && (
              <BookingInfoPanel
                teeTimeLabel={teeTimeLabel}
                isProblematic={isProblematic}
                isValidated={isValidated}
                warnings={warnings}
                warningsLastChecked={warningsLastChecked}
                onRecheck={handleRecheck}
                onApprove={isSupervisorOrAdmin(user) ? handleApprove : null}
                recheckLoading={false}
                approveLoading={false}
                disabled={isBusy}
                isCancelled={isCancelled}
                isArchived={isArchived}
              />
            )}

            {/* Booked by */}
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="bkf-booked-by">
                Booked by
              </label>
              <select
                id="bkf-booked-by"
                className={formStyles.selectInput}
                value={bookedByMemberId ?? ''}
                onChange={(e) => setBookedByMemberId(e.target.value ? Number(e.target.value) : null)}
                disabled={isBusy}
              >
                {players.filter((p) => p.type === 'member' && p.memberId).length === 0 ? (
                  <option value="">— add a member player first —</option>
                ) : (
                  players
                    .filter((p) => p.type === 'member' && p.memberId)
                    .map((p) => (
                      <option key={p.memberId} value={p.memberId}>{p.memberName}</option>
                    ))
                )}
              </select>
            </div>

            {/* Players section */}
            <div className={formStyles.field}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionLabel}>
                  Players <span style={{ color: 'var(--cp-danger)', marginLeft: 2 }} aria-hidden="true">*</span>
                </span>
                <button
                  type="button"
                  className={styles.addPlayerBtn}
                  onClick={handleAddPlayer}
                  disabled={isBusy}
                >
                  <Plus size={13} strokeWidth={1.5} />
                  Add player
                </button>
              </div>

              <div className={styles.playersList}>
                {players.map((player, idx) => {
                  const estArrival = computeEstArrival(player.holes, teeTimeHole, teeTimeStartMins, nineHolePace);
                  return (
                    <div key={idx} className={styles.playerEntry}>
                      <PlayerRow
                        player={player}
                        idx={idx}
                        onChange={handlePlayerChange}
                        onMemberSelect={handleMemberSelect}
                        onRemove={handleRemovePlayer}
                        canRemove={players.length > 1}
                        disabled={isBusy}
                        showStatus={true}
                      />
                      {estArrival && (
                        <p className={styles.estArrival}>Est. arrival at Hole {estArrival.destHole}: {estArrival.time}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {fieldErrors.players && (
                <p className={styles.playersError} role="alert">{fieldErrors.players}</p>
              )}
            </div>
      </EntityForm>

      {toasts.length > 0 &&
        createPortal(
          <div className={styles.toastContainer} aria-live="polite">
            {toasts.map((t) => (
              <div
                key={t.id}
                className={[
                  styles.toast,
                  t.type === 'error' ? styles.toastError : '',
                ].join(' ').trim()}
                role="status"
              >
                {t.message}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}
